import {
  FiMail,
  FiMessageCircle,
  FiClock,
  FiGitBranch,
  FiFileText,
  FiArrowRight,
  FiCheckSquare,
  FiEdit3,
  FiTag,
  FiSend,
  FiBell,
  FiUserPlus,
  FiUserX,
  FiGitMerge,
  FiShuffle,
  FiTarget,
  FiCalendar,
  FiPrinter,
  FiGrid,
} from 'react-icons/fi';

export const ICON_MAP = {
  SEND_EMAIL: FiMail,
  SEND_SMS: FiMessageCircle,
  SEND_POSTCARD: FiPrinter,
  WAIT: FiClock,
  WAIT_FOR: FiTarget,
  WAIT_UNTIL_MEETING: FiCalendar,
  IF_ELSE: FiGitBranch,
  ADD_NOTE: FiFileText,
  UPDATE_STAGE: FiArrowRight,
  CREATE_TASK: FiCheckSquare,
  UPDATE_DEAL_FIELD: FiEdit3,
  MANAGE_TAG: FiTag,
  SEND_WEBHOOK: FiSend,
  APPEND_SHEET_ROW: FiGrid,
  SEND_NOTIFICATION: FiBell,
  ENROLL_CAMPAIGN: FiUserPlus,
  UNENROLL_CAMPAIGN: FiUserX,
  SWITCH: FiGitMerge,
  AB_SPLIT: FiShuffle,
};

// Hex colors for canvas / flow rendering
export const COLOR_MAP_HEX = {
  SEND_EMAIL: '#38A169',
  SEND_SMS: '#38A169',
  SEND_NOTIFICATION: '#38A169',
  SEND_POSTCARD: '#38A169',
  WAIT: '#DD6B20',
  WAIT_FOR: '#DD6B20',
  WAIT_UNTIL_MEETING: '#DD6B20',
  IF_ELSE: '#805AD5',
  SWITCH: '#805AD5',
  AB_SPLIT: '#805AD5',
  ADD_NOTE: '#319795',
  UPDATE_STAGE: '#319795',
  CREATE_TASK: '#319795',
  UPDATE_DEAL_FIELD: '#319795',
  MANAGE_TAG: '#319795',
  SEND_WEBHOOK: '#D69E2E',
  APPEND_SHEET_ROW: '#38A169',
  ENROLL_CAMPAIGN: '#3182CE',
  UNENROLL_CAMPAIGN: '#E53E3E',
};

// Chakra colorScheme tokens for badges / UI
export const COLOR_MAP = {
  SEND_EMAIL: 'green',
  SEND_SMS: 'green',
  SEND_NOTIFICATION: 'green',
  SEND_POSTCARD: 'green',
  WAIT: 'orange',
  WAIT_FOR: 'orange',
  WAIT_UNTIL_MEETING: 'orange',
  IF_ELSE: 'purple',
  SWITCH: 'purple',
  AB_SPLIT: 'purple',
  ADD_NOTE: 'teal',
  UPDATE_STAGE: 'teal',
  CREATE_TASK: 'teal',
  UPDATE_DEAL_FIELD: 'teal',
  MANAGE_TAG: 'teal',
  SEND_WEBHOOK: 'yellow',
  APPEND_SHEET_ROW: 'green',
  ENROLL_CAMPAIGN: 'blue',
  UNENROLL_CAMPAIGN: 'red',
};

export const STATUS_COLOR = {
  pending: 'yellow',
  processing: 'blue',
  completed: 'green',
  failed: 'red',
  paused: 'gray',
  cancelled: 'gray',
};

export const NODE_LABELS = {
  SEND_EMAIL: 'Send Email',
  SEND_SMS: 'Send SMS',
  SEND_NOTIFICATION: 'Send Notification',
  SEND_POSTCARD: 'Send Postcard',
  WAIT: 'Wait',
  WAIT_FOR: 'Wait For Condition',
  WAIT_UNTIL_MEETING: 'Wait Until Meeting',
  IF_ELSE: 'If / Else',
  SWITCH: 'Switch',
  AB_SPLIT: 'A/B Split',
  ADD_NOTE: 'Add Note',
  UPDATE_STAGE: 'Update Stage',
  CREATE_TASK: 'Create Task',
  UPDATE_DEAL_FIELD: 'Update Deal Field',
  MANAGE_TAG: 'Manage Tag',
  SEND_WEBHOOK: 'Send Webhook',
  APPEND_SHEET_ROW: 'Append to Google Sheet',
  ENROLL_CAMPAIGN: 'Enroll in Campaign',
  UNENROLL_CAMPAIGN: 'Unenroll from Campaign',
};

export function getSummary(node) {
  const c = node.config || {};
  switch (node.subType) {
    case 'SEND_EMAIL': return c.subject || 'No subject';
    case 'SEND_SMS': {
      const phone = c.phoneField === 'secondaryPhone' ? '2nd phone'
        : c.phoneField === 'custom' ? (c.customPhone || 'custom #')
        : 'primary phone';
      const msg = c.message ? c.message.slice(0, 30) : 'No message';
      return `${msg} (${phone})`;
    }
    case 'SEND_POSTCARD': {
      if (!c.designId) return 'No design selected';
      return `${c.designName || `Design #${c.designId}`} (${c.mailClass === 'Standard' ? 'Standard' : 'First Class'})`;
    }
    case 'WAIT': return `${c.duration || 0} ${c.unit || 'days'}`;
    case 'WAIT_FOR': return c.field ? `Wait for ${c.field} ${c.operator || 'equals'} ${c.value || ''}`.trim() : 'No condition';
    case 'WAIT_UNTIL_MEETING': {
      const dur = c.duration || 0;
      if (!dur) return 'Until meeting start';
      return `${dur} ${c.unit || 'hours'} ${c.direction === 'after' ? 'after' : 'before'} meeting`;
    }
    case 'IF_ELSE': return c.field ? `If ${c.field} ${c.operator || 'exists'}` : 'No condition';
    case 'ADD_NOTE': return c.text ? c.text.slice(0, 40) : 'No note';
    case 'UPDATE_STAGE': return c.stage || 'No stage';
    case 'CREATE_TASK': return c.title || 'No title';
    case 'MANAGE_TAG': {
      if (!c.tagName) return 'No tag';
      const triggers = c.triggerCampaigns === true || c.triggerCampaigns === 'true';
      return `${c.action} ${c.tagName}${triggers ? ' (triggers campaigns)' : ''}`;
    }
    case 'SEND_WEBHOOK': return c.url ? c.url.slice(0, 30) : 'No URL';
    case 'APPEND_SHEET_ROW': {
      if (!c.spreadsheetId) return 'No spreadsheet';
      const cols = Array.isArray(c.columns) ? c.columns.length : 0;
      return `${c.sheetName || 'First tab'} (${cols} column${cols === 1 ? '' : 's'})`;
    }
    case 'AB_SPLIT': return `${c.splitPercent || 50}% / ${100 - (c.splitPercent || 50)}%`;
    default: return (node.subType || '').replace(/_/g, ' ');
  }
}

export function getNodeLabel(subType) {
  return NODE_LABELS[subType] || (subType || '').replace(/_/g, ' ');
}

export function getTriggerDescription(triggerType, triggerConfig) {
  const c = triggerConfig || {};
  switch (triggerType) {
    case 'TAG_ADDED': {
      const loc = c.tagScope === 'deal' ? ' on a deal' : c.tagScope === 'either' ? ' on a contact or deal' : '';
      return c.tagName ? `Triggers when tag "${c.tagName}" is added${loc}` : `Triggers when any tag is added${loc}`;
    }
    case 'TAG_REMOVED': {
      const loc = c.tagScope === 'deal' ? ' on a deal' : c.tagScope === 'either' ? ' on a contact or deal' : '';
      return c.tagName ? `Triggers when tag "${c.tagName}" is removed${loc}` : `Triggers when any tag is removed${loc}`;
    }
    case 'PIPELINE_STAGE_CHANGED': {
      if (c.fromStage && c.toStage) return `Triggers on stage change from "${c.fromStage}" to "${c.toStage}"`;
      if (c.toStage) return `Triggers when stage changes to "${c.toStage}"`;
      if (c.fromStage) return `Triggers when stage changes from "${c.fromStage}"`;
      return 'Triggers on any stage change';
    }
    case 'CONTACT_CREATED':
      return c.source ? `Triggers when a contact is created from "${c.source}"` : 'Triggers when a new contact is created';
    case 'CONTACT_UPDATED':
      return c.fieldName ? `Triggers when "${c.fieldName}" is updated` : 'Triggers when a contact is updated';
    case 'DEAL_CREATED':
      return 'Triggers when a new deal is created';
    case 'DEAL_FIELD_UPDATED':
      return c.fieldName ? `Triggers when deal field "${c.fieldName}" is updated` : 'Triggers when any deal field is updated';
    case 'DEAL_ASSIGNED':
      return 'Triggers when a deal is assigned';
    case 'DEAL_STALLED':
      return 'Triggers when a deal becomes stalled';
    case 'DISPOSITION_LOGGED':
      return c.disposition ? `Triggers when disposition "${c.disposition}" is logged` : 'Triggers when any disposition is logged';
    case 'MANUAL_ADD':
      return 'Manually triggered enrollment';
    case 'WEBHOOK_RECEIVED':
      return 'Triggers on incoming webhook';
    case 'MEETING_BOOKED':
      return c.eventTypeSlug ? `Triggers when a "${c.eventTypeSlug}" meeting is booked` : 'Triggers when a meeting is booked';
    case 'MEETING_RESCHEDULED':
      return c.eventTypeSlug ? `Triggers when a "${c.eventTypeSlug}" meeting is rescheduled` : 'Triggers when a meeting is rescheduled';
    case 'MEETING_CANCELLED':
      return c.eventTypeSlug ? `Triggers when a "${c.eventTypeSlug}" meeting is cancelled` : 'Triggers when a meeting is cancelled';
    case 'MEETING_NO_SHOW':
      return c.eventTypeSlug ? `Triggers when a "${c.eventTypeSlug}" meeting is marked no-show` : 'Triggers when a meeting is marked no-show';
    default:
      return `Triggers on ${(triggerType || '').replace(/_/g, ' ').toLowerCase()}`;
  }
}
